// New folder and Edit folder, after SFolders: Name, Description (the assistant reads it first), Where (Team-wide or
// a space), and in Edit, Delete folder with a plain warning. Partners see every folder (read only), so there is no
// partner option.
// The same form on a phone, in a bottom sheet with the phone's fields.
import { useState, type FormEvent } from 'react';
import { shownSpaceColor } from '@dpw/shared';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import type { Folder } from '@dpw/shared';
import { api } from '../api';
import { useSpaces } from '../lib/queries';
import { Sheet } from '../ui/Sheet';
import { useToast } from '../ui/toast';
import { PSheet } from '../phone/kit';
import { scopeDot } from './model';
import { filesText } from './parts';

export function FolderForm({ edit, spaceId, onClose, phone }: { edit?: Folder; spaceId?: string | null; onClose: () => void; phone?: boolean }) {
  const spaces = useSpaces().data ?? [];
  const nav = useNavigate();
  const qc = useQueryClient();
  const toast = useToast();
  const [name, setName] = useState(edit?.name ?? '');
  const [desc, setDesc] = useState(edit?.description ?? '');
  const [where, setWhere] = useState<string | null>(edit ? edit.spaceId : spaceId ?? null);
  const [err, setErr] = useState('');
  const [delAsk, setDelAsk] = useState(false);
  const [busy, setBusy] = useState(false);
  const title = edit ? 'Edit folder' : 'New folder';
  const whereName = where ? spaces.find(s => s.id === where)?.name ?? 'this space' : null;
  const hint = !where ? 'Everyone on the team can see it and add files. The assistant can use it anywhere.'
    : `Everyone on ${whereName} can see it and add files. The assistant uses it for questions about ${whereName}.`;

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) { setErr('Give the folder a name.'); return; }
    setBusy(true); setErr('');
    const body = { name: name.trim(), description: desc.trim(), spaceId: where };
    const go = edit ? api.knowledge.updateFolder(edit.id, body) : api.knowledge.createFolder(body);
    go.then(f => {
      void qc.invalidateQueries();
      onClose();
      if (edit) toast('Folder saved.');
      else { nav(`/folders/${f.id}`); toast('Folder created. Add files, or attach them in the Assistant and name this folder.'); }
    }, e2 => setErr(e2 instanceof Error ? e2.message : 'That did not work.')).finally(() => setBusy(false));
  };
  const del = () => {
    if (!edit) return;
    api.knowledge.deleteFolder(edit.id).then(() => { void qc.invalidateQueries(); onClose(); nav('/folders'); toast(`Deleted ${edit.name}.`); }, e => setErr(e instanceof Error ? e.message : 'That did not work.'));
  };
  const wheres = [{ id: null as string | null, label: 'Team-wide', dot: scopeDot(null) }, ...spaces.map(s => ({ id: s.id as string | null, label: s.name, dot: scopeDot(shownSpaceColor(s.color)) }))];

  if (phone) return (
    <PSheet label={title} onClose={onClose}>
      <h2>{title}</h2>
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div className="ph-fl3">Name</div><input className="ph-inp" value={name} onChange={e => setName(e.target.value)} placeholder="For example: Vendor contracts" aria-label="Name" autoComplete="off" />
        {err && <div className="f13" style={{ color: 'var(--red-tx)' }} role="alert">{err}</div>}
        <div className="ph-fl3">Description</div><textarea className="ph-inp" value={desc} onChange={e => setDesc(e.target.value)} placeholder="What is in it, and when to use it" aria-label="Description" />
        <div className="xs mut" style={{ marginTop: -4 }}>The assistant reads this first to decide whether to open the folder.</div>
        <div className="ph-fl3">Where</div>
        <div className="ph-chips" role="group" aria-label="Where">{wheres.map(w => (
          <button key={w.id ?? 'team'} type="button" className={`ph-chip${where === w.id ? ' on' : ''}`} aria-pressed={where === w.id} onClick={() => setWhere(w.id)}><i style={{ background: w.dot }} />{w.label}</button>
        ))}</div>
        <button className="ph-btf" style={{ marginTop: 8 }} disabled={busy}>{edit ? 'Save folder' : 'Create folder'}</button>
      </form>
    </PSheet>
  );

  return (
    <Sheet crumbs={<><span>Knowledge base</span><span>/</span><b>{title}</b></>} onClose={onClose} width={560} label={title}>
      <h2 className="stitle" style={{ fontSize: 24 }}>{title}</h2>
      <form className="nform" onSubmit={submit}>
        <label className="fld">Name<input className="inp" value={name} onChange={e => setName(e.target.value)} placeholder="For example: Vendor contracts" autoComplete="off" data-autofocus /></label>
        {err && <span className="errt" role="alert">{err}</span>}
        <label className="fld">Description<textarea className="inp" value={desc} onChange={e => setDesc(e.target.value)} placeholder="What is in it, and when to use it" /><span className="xs mut">The assistant reads this first to decide whether to open the folder.</span></label>
        <div className="fld">Where<div className="fchips" role="group" aria-label="Where">{wheres.map(w => (
          <button key={w.id ?? 'team'} type="button" className={`fc wh${where === w.id ? ' on' : ''}`} aria-pressed={where === w.id} onClick={() => setWhere(w.id)}><i style={{ background: w.dot }} />{w.label}</button>
        ))}</div><span className="xs mut">{hint}</span></div>
        <div className="rowc"><button className="bt pri" disabled={busy}>{edit ? 'Save folder' : 'Create folder'}</button><button type="button" className="bt" onClick={onClose}>Cancel</button>
          {edit?.canEdit && <><span style={{ flex: 1 }} /><button type="button" className="bt" onClick={() => setDelAsk(true)}>Delete folder</button></>}</div>
        {delAsk && edit && <div className="cfm"><span>Delete {edit.name}? Its {filesText(edit.fileCount)} {edit.fileCount === 1 ? 'is' : 'are'} unlinked from it. A file linked to an activity, a track or another folder stays there.</span>
          <button type="button" className="bt pri sm" onClick={del}>Delete folder</button><button type="button" className="bt sm" onClick={() => setDelAsk(false)}>Keep it</button></div>}
      </form>
    </Sheet>
  );
}
